import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AiAssessmentService, StartAssessmentResponse } from '../../services/ai-assessment.service';
import { PatProvider, UsersService } from '../../services/users.service';
import { HttpErrorFormatter } from '../../services/http-error.formatter';

import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../spinner/spinner.component';

export interface OrionRepository {
    project: string;
    repository: string;
    scm: string;
    repositoryUrl: string;
    displayName: string;
}

export interface StartAssessmentDialogData {
    appName: string;
    appId: string;
    trackedBusinessApplicationId: number;
    orionManifestJson: string | null;
    mode?: 'full' | 'lightweight';
}

export interface StartAssessmentDialogResult {
    jobId: number;
    queuePosition: number;
}

@Component({
    selector: 'app-start-assessment-dialog',
    templateUrl: './start-assessment-dialog.component.html',
    styleUrls: ['./start-assessment-dialog.component.css'],
    imports: [FormsModule, MaterialModule, CommonModule, SpinnerComponent]
})
export class StartAssessmentDialogComponent implements OnInit, OnDestroy {
    private readonly defaultGitHubOrg = 'Chevron';
    private readonly defaultAdoBranch = 'master';
    private readonly defaultGitHubBranch = 'main';

    repositories: OrionRepository[] = [];
    selectedRepository: string = '';
    resolvedProvider: string | null = null;  // Resolved provider ('github' or 'azure-devops')
    branch: string = this.defaultAdoBranch;

    // PAT state
    patStatus: 'unselected' | 'loading' | 'missing' | 'configured' = 'unselected';

    // Submission state
    isSubmitting: boolean = false;
    submissionResult: StartAssessmentResponse | null = null;
    errorMessage: string | null = null;
    autoCloseCountdown: number = 5;
    private autoCloseInterval: any = null;

    constructor(
        public dialogRef: MatDialogRef<StartAssessmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: StartAssessmentDialogData,
        private aiAssessmentService: AiAssessmentService,
        private usersService: UsersService
    ) { }

    ngOnInit(): void {
        this.parseRepositories();
    }

    ngOnDestroy(): void {
        this.clearAutoClose();
    }

    get isLightweightMode(): boolean {
        return this.data.mode === 'lightweight';
    }

    get assessmentName(): string {
        return this.isLightweightMode ? 'Lightweight Code Validation' : 'AI Code Assessment';
    }

    private startAutoClose(): void {
        this.autoCloseCountdown = 5;
        this.autoCloseInterval = setInterval(() => {
            this.autoCloseCountdown--;
            if (this.autoCloseCountdown <= 0) {
                this.clearAutoClose();
                this.onClose();
            }
        }, 1000);
    }

    private clearAutoClose(): void {
        if (this.autoCloseInterval) {
            clearInterval(this.autoCloseInterval);
            this.autoCloseInterval = null;
        }
    }

    private parseRepositories(): void {
        if (!this.data.orionManifestJson) return;
        try {
            const manifest = JSON.parse(this.data.orionManifestJson);
            const repositories = manifest?.application?.repositories;
            if (Array.isArray(repositories) && repositories.length > 0) {
                this.repositories = repositories
                    .map((r: any) => this.mapOrionRepository(r))
                    .filter((r: OrionRepository) => !!r.repositoryUrl);

            }
        } catch (e) {
            console.warn('Failed to parse Orion manifest JSON', e);
        }
    }

    private mapOrionRepository(r: any): OrionRepository {
        const project = (r?.project || '').trim();
        const repository = (r?.repository || '').trim();
        const scm = (r?.scm || '').trim();
        const repositoryUrl = this.buildRepositoryUrl(project, repository, scm);
        const displayName = this.buildDisplayName(project, repository, scm, repositoryUrl);

        return {
            project,
            repository,
            scm,
            repositoryUrl,
            displayName
        };
    }

    private buildRepositoryUrl(project: string, repository: string, scm: string): string {
        const provider = this.normalizeScm(scm);

        if (provider === 'github') {
            const normalizedRepository = repository.replace(/^\/+/, '').trim();
            if (!normalizedRepository) return '';

            if (normalizedRepository.startsWith('https://') || normalizedRepository.startsWith('http://') || normalizedRepository.startsWith('git@')) {
                return normalizedRepository;
            }

            if (normalizedRepository.includes('/')) {
                return `https://github.com/${normalizedRepository}`;
            }

            return `https://github.com/${this.defaultGitHubOrg}/${normalizedRepository}`;
        }

        if (project && repository) {
            return `${project}/${repository}`;
        }

        return repository;
    }

    private buildDisplayName(project: string, repository: string, scm: string, repositoryUrl: string): string {
        const provider = this.normalizeScm(scm);
        const providerLabel = provider === 'github' ? 'GitHub' : 'ADO';

        if (provider === 'github') {
            return `${repository || repositoryUrl} (${providerLabel})`;
        }

        if (project && repository) {
            return `${project}/${repository} (${providerLabel})`;
        }

        return `${repositoryUrl} (${providerLabel})`;
    }

    private normalizeScm(scm: string): 'github' | 'ado' {
        const normalized = (scm || '').trim().toLowerCase();
        return normalized.includes('github') ? 'github' : 'ado';
    }

    private getSelectedRepositoryEntry(repositoryUrl?: string): OrionRepository | null {
        const effectiveRepositoryUrl = repositoryUrl ?? this.selectedRepository;
        if (!effectiveRepositoryUrl) return null;
        return this.repositories.find(r => r.repositoryUrl === effectiveRepositoryUrl) || null;
    }

    private getPatProviderForRepository(repositoryUrl?: string): PatProvider {
        const effectiveRepositoryUrl = repositoryUrl ?? this.selectedRepository;
        const selected = this.getSelectedRepositoryEntry(effectiveRepositoryUrl);
        if (selected && this.normalizeScm(selected.scm) === 'github') {
            return 'github';
        }

        if (this.isGitHubUrl(effectiveRepositoryUrl)) {
            return 'github';
        }

        return 'azure-devops';
    }

    private isGitHubUrl(url: string): boolean {
        if (!url) return false;

        // SSH form: git@github.com:org/repo.git
        const sshMatch = /^git@([^:/]+):/i.exec(url.trim());
        if (sshMatch) {
            return this.isGitHubHost(sshMatch[1]);
        }

        try {
            const parsed = new URL(url);
            return this.isGitHubHost(parsed.hostname);
        } catch {
            return false;
        }
    }

    private isGitHubHost(host: string): boolean {
        const normalizedHost = (host || '').trim().toLowerCase();
        return normalizedHost === 'github.com' || normalizedHost.endsWith('.github.com');
    }

    private setDefaultBranchForRepository(repositoryUrl?: string): void {
        const provider = this.getPatProviderForRepository(repositoryUrl);
        this.resolvedProvider = provider;  // Track the resolved provider
        this.branch = provider === 'github' ? this.defaultGitHubBranch : this.defaultAdoBranch;
    }

    get currentProviderDisplayName(): string {
        return this.getPatProviderForRepository() === 'github' ? 'GitHub' : 'Azure DevOps';
    }

    onRepositoryChange(selectedRepository: string): void {
        this.selectedRepository = selectedRepository;
        this.setDefaultBranchForRepository(selectedRepository);
        this.checkPatStatus();
    }

    private checkPatStatus(): void {
        this.patStatus = 'loading';

        this.usersService.getPatStatus(this.getPatProviderForRepository()).subscribe({
            next: (status) => {
                this.patStatus = status.hasPatConfigured ? 'configured' : 'missing';
            },
            error: () => {
                // Keep the assessment blocked if PAT status cannot be confirmed.
                this.patStatus = 'missing';
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onStart(): void {
        if (!this.isValid() || this.isSubmitting) return;

        this.isSubmitting = true;
        this.errorMessage = null;

        const request = {
            repositoryUrl: this.selectedRepository.trim(),
            branch: this.branch.trim(),
            orionId: this.data.appId,
            trackedBusinessApplicationId: this.data.trackedBusinessApplicationId,
            repositoryProvider: this.resolvedProvider  // Include resolved provider
        };

        const start$ = this.isLightweightMode
            ? this.aiAssessmentService.startLightweightValidation(request)
            : this.aiAssessmentService.startAssessment(request);

        start$.subscribe({
            next: (response) => {
                this.isSubmitting = false;
                this.submissionResult = response;
                this.startAutoClose();
            },
            error: (err) => {
                this.isSubmitting = false;
                const formatted = HttpErrorFormatter.formatError(err);
                
                // Display user-friendly error message
                if (formatted.isThrottled) {
                    // Rate limit error
                    this.errorMessage = `Too many assessment requests. Please wait ${formatted.retryAfterSeconds || 60} seconds before trying again.`;
                } else if (formatted.isAuthFailure) {
                    // Authentication or permission error
                    this.errorMessage = formatted.message;
                } else if (formatted.isNetworkFailure) {
                    // Network error
                    this.errorMessage = 'Network connection failed. Please check your connection and try again.';
                } else {
                    // Generic error with detail if available
                    this.errorMessage = formatted.message;
                    if (formatted.detail && formatted.detail !== formatted.message) {
                        this.errorMessage += ` (${formatted.detail})`;
                    }
                }

                // Log full error info for debugging
                console.error('Assessment start error:', formatted);
                if (formatted.traceId) {
                    console.error('Trace ID for support:', formatted.traceId);
                }
            }
        });
    }

    onClose(): void {
        if (this.submissionResult) {
            const result: StartAssessmentDialogResult = {
                jobId: this.submissionResult.jobId,
                queuePosition: this.submissionResult.queuePosition
            };
            this.dialogRef.close(result);
        } else {
            this.dialogRef.close();
        }
    }

    isValid(): boolean {
        return this.patStatus === 'configured'
            && this.selectedRepository.trim().length > 0
            && this.branch.trim().length > 0;
    }

    get hasRepositories(): boolean {
        return this.repositories.length > 0;
    }
}
